import classNames from 'classnames';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import ReactTooltip from 'react-tooltip';
import { PDFPageProxy } from 'pdfjs-dist/webpack';
import React, { useState, useEffect } from 'react';
import { useInViewEffect } from 'react-hook-inview';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import backArrow from '../assets/img/pdf-tool/svg/back.svg';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Panels } from '../pdf-tool-split/pdf-tool-split-panels-component';
import { ThumbnailGroup } from '../pdf-tool-split/pdf-tool-split-thumbnail-group-component';
import { useAppDispatch } from '../store/store';
import { Toolbar } from '../toolbar/toolbar-component';

import {
  selectSplitPanelVisible,
  selectSplitRanges,
  selectExtractPanelVisible,
  setSelectedPdf,
  addPage,
  selectPages,
  selectSelectedPDF,
  togglePageSelected,
  showSelectPages,
  setFileName,
  selectIsSplitting,
} from './pdf-slice';
import { userSelector } from '../user/user-slice';

require('./pdf-tool-manage.scss');

export interface PdfToolManageStateProps {}

export interface PdfToolManageDispatchProps {}

export interface PdfToolManageOwnProps {}

export interface PdfToolManageProps
  extends PdfToolManageStateProps,
    PdfToolManageDispatchProps,
    PdfToolManageOwnProps {
  history: any;
  match: any;
  location: any;
}

const PdfPageCard = ({ page, pdfDoc, onClick }) => {
  const [inView, setInView] = useState(false);

  const getSelectPages = useSelector(selectPages);

  const ref = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
      }
      setInView(entry.isIntersecting);
    },
    { threshold: 0.5 },
  );

  useEffect(() => {
    if (inView) {
      const canvas: any = document.getElementById(`canvas-page-${page}`);
      if (canvas === null) {
        return;
      }

      const pagePromise = pdfDoc.getPage(page);
      pagePromise.then((page: PDFPageProxy) => {
        const viewport = page.getViewport({ scale: 1 });
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        // Render PDF page into canvas context
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.beginPath();
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };
        page.render(renderContext);
      });
    }
  }, [inView]);

  return (
    <div
      id={`pdf-page-${page}`}
      ref={ref}
      className={classNames('l-thumbnail-container')}
    >
      <div
        className={classNames('l-thumbnail', {
          'l-thumbnail--selected':
            getSelectPages[page] && getSelectPages[page].selected,
        })}
        onClick={onClick}
      >
        <div className="l-thumbnail__selection-ring">
          <canvas
            id={`canvas-page-${page}`}
            className="l-thumbnail__canvas"
          ></canvas>
        </div>
      </div>
      <div className="l-thumbnail__number">{page}</div>
    </div>
  );
};

export const PdfToolManageComponent: React.FC<PdfToolManageProps> = ({
  match,
  location,
}) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pagesLoaded, setPagesLoaded] = useState(false);

  const getSelectSplitRangePanelVisible = useSelector(selectSplitPanelVisible);
  const getSelectSplitRanges = useSelector(selectSplitRanges);
  const getSelectExtractPanelVisible = useSelector(selectExtractPanelVisible);
  const getSelectedPDF = useSelector(selectSelectedPDF);
  const isSplitting = useSelector(selectIsSplitting);
  const user = useSelector(userSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const url = `/api/v2/mounts/${match.params.mountId}/files/info${location.search}`;
    fetch(url)
      .then((res) => res.json())
      .then((r) => {
        dispatch(setFileName(r.name.split('.pdf')[0]));
      });
  }, []);

  useEffect(() => {
    ReactTooltip.hide();
  });

  function parentPath(path) {
    const pathParts = path.split('%2F');
    pathParts.pop();
    return `${pathParts.join('/')}`;
  }

  useEffect(() => {
    if (!getSelectExtractPanelVisible || isSplitting) {
      return;
    }
    const url = `/content/api/v2/mounts/${match.params.mountId}/files/get${location.search}`;
    const loadingTask = pdfjsLib.getDocument(url);
    loadingTask.promise.then(
      (pdf) => {
        console.log('PDF loaded');
        const allPages = Array(...Array(pdf.numPages)).map((_, i) => i + 1);
        setPdfDoc(pdf);
        dispatch(
          setSelectedPdf({
            pages: pdf.numPages,
            url: url,
            name: '',
            path: parentPath(location.search.split('?path=')[1]),
            mountId: match.params.mountId,
          }),
        );
        allPages.forEach((pageNumber) => {
          dispatch(
            addPage({
              page: pageNumber,
              thumbnail: '',
            }),
          );
        });
        setPagesLoaded(true);
      },
      (reason) => {
        // PDF loading error
        console.log(reason);
      },
    );
  }, [getSelectExtractPanelVisible, isSplitting]);

  const handlePdfCardClick = (e, p: number) => {
    e.preventDefault();
    dispatch(togglePageSelected(p));
    dispatch(showSelectPages());
  };

  return (
    user ? (
      <div className="pdf-tool-manage">
        <Toolbar>
          <div className="l-toolbar__left">
            <Link to="/pdf-tool/split/" className="pdf-tool-split__navbar-left">
              <div className="l-toolbar__icon">
                <img src={backArrow} alt="Back arrow"></img>
              </div>
            </Link>
            <Link to="/pdf-tool/" className="l-toolbar__title">
              Split
            </Link>
          </div>
        </Toolbar>
        <div className="pdf-tool-manage__container">
          {!isSplitting ? (
            <>
              <div className="pdf-tool-manage__thumbnails-ranges-container">
                {getSelectSplitRangePanelVisible ? (
                  <>
                    {getSelectSplitRanges.map((range) => (
                      <ThumbnailGroup
                        range={range}
                        key={`group ${range.name}`}
                        pdf={pdfDoc}
                      />
                    ))}
                  </>
                ) : null}
              </div>
              {getSelectExtractPanelVisible && getSelectedPDF ? (
                <div
                  className="pdf-tool-manage__pages-container"
                  id="mainContainer"
                >
                  {pagesLoaded ? (
                    <>
                      {[...Array(getSelectedPDF.pages).keys()].map((i) => (
                        <PdfPageCard
                          page={i + 1}
                          pdfDoc={pdfDoc}
                          onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                            handlePdfCardClick(e, i + 1)
                          }
                          key={`pthumb-${i + 1}`}
                        />
                      ))}
                    </>
                  ) : (
                    <LoadingSpinner />
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <Panels />
      </div>
    ) : null
  );
};
